import React, { useState } from 'react';
import {
    Plus, Shield, Edit3, ChevronDown,
    Trash2, FileText, Layers, X, PlusCircle, Copy, Archive, CheckCircle2, AlertCircle, AlertTriangle,
    ArrowRight, ArrowLeft, Save
} from 'lucide-react';
import ReusableTable from '../components/ReusableTable';

const Framework = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // View & Table States
    const [viewingFramework, setViewingFramework] = useState(null);
    const [showControlsTable, setShowControlsTable] = useState(false);
    const [frameworkToDelete, setFrameworkToDelete] = useState(null);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Add/Edit Category States
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '' });

    // Edit Mode States
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Default empty control template 
    const defaultControl = {
        id: Date.now().toString(),
        category: '',
        title: '',
        scopeRef: '',
        complianceRef: '',
        globalStandard: '',
        controlExpected: '',
        parameters: '',
        evidence: '',
        owner: '',
        status: 'Not Started'
    };

    const [newCategoryControls, setNewCategoryControls] = useState([{ ...defaultControl }]);
    const [controls, setControls] = useState([{ ...defaultControl }]);

    // State to store the list of frameworks
    const [frameworks, setFrameworks] = useState([
        {
            id: '1',
            name: 'SOC 2 Type II',
            description: 'Security compliance framework',
            type: 'standard',
            version: 'SOC2 2017',
            owner: 'Compliance Team',
            projects: ['Alpha Dashboard'],
            status: 'Active',
            tags: ['Standard', 'Security', 'Active'],
            progress: 65,
            issuesCount: 3,
            passingCount: 0,
            categories: [
                {
                    id: 'cat-1',
                    name: 'Security Domain',
                    description: 'Core security and access controls mapped to SOC2.',
                    controls: [
                        { id: 'c1', title: 'Logical Access', scopeRef: 'All Systems', complianceRef: 'CC6.1', globalStandard: 'TSC 2017', controlExpected: 'Logical access controls must be in place to prevent unauthorized access.', parameters: 'In-Scope Only', evidence: 'Access logs, Access review matrix', owner: 'DevOps', status: 'Passing' },
                    ]
                }
            ]
        },
        {
            id: '2',
            name: 'ISO 27001',
            description: 'Information security management system standard.',
            type: 'standard',
            version: '2022',
            owner: 'Security Team',
            projects: ['All Projects'],
            status: 'Draft',
            tags: ['Standard', 'Security', 'Draft'],
            progress: 0,
            issuesCount: 0,
            passingCount: 0,
            categories: [
                {
                    id: 'cat-2',
                    name: 'Information Classification',
                    description: 'Policies for handling and labeling data.',
                    controls: [
                        { id: 'c3', title: 'Data Labeling', scopeRef: 'EU Customer Data', complianceRef: 'A.8.1', globalStandard: 'ISO 27001', controlExpected: 'Information must be classified.', parameters: 'All Information Assets', evidence: 'Classification policy', owner: 'Security', status: 'Not Started' }
                    ]
                }
            ]
        }
    ]);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'custom',
        version: '',
        owner: '',
        projects: '',
        status: 'Draft',
        category: ''
    });

    // --- HANDLERS ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleControlChange = (id, field, value) => {
        setControls(prev => prev.map(ctrl => ctrl.id === id ? { ...ctrl, [field]: value } : ctrl));
    };
    const addControl = () => setControls(prev => [...prev, { ...defaultControl, id: Date.now().toString() + Math.random() }]);
    const removeControl = (id) => { if (controls.length > 1) setControls(prev => prev.filter(ctrl => ctrl.id !== id)); };

    const handleNewCategoryControlChange = (id, field, value) => {
        setNewCategoryControls(prev => prev.map(ctrl => ctrl.id === id ? { ...ctrl, [field]: value } : ctrl));
    };
    const addNewCategoryControl = () => setNewCategoryControls(prev => [...prev, { ...defaultControl, id: Date.now().toString() + Math.random() }]);
    const removeNewCategoryControl = (id) => { if (newCategoryControls.length > 1) setNewCategoryControls(prev => prev.filter(ctrl => ctrl.id !== id)); };

    const handleEditCategoryStart = (category, e) => {
        e.stopPropagation();
        setIsAddingCategory(true);
        setEditingCategoryId(category.id);
        setNewCategoryData({ name: category.name, description: category.description });
        setNewCategoryControls(JSON.parse(JSON.stringify(category.controls)));
    };

    const confirmDeleteCategory = (category, e) => {
        e.stopPropagation();
        setCategoryToDelete(category);
    };

    const executeDeleteCategory = () => {
        if (!categoryToDelete || !viewingFramework) return;
        const updatedCategories = viewingFramework.categories.filter(c => c.id !== categoryToDelete.id);
        setViewingFramework({ ...viewingFramework, categories: updatedCategories });
        setFrameworks(prev => prev.map(fw =>
            fw.id === viewingFramework.id ? { ...fw, categories: updatedCategories } : fw
        ));
        setCategoryToDelete(null);
    };

    const handleSaveNewCategory = () => {
        if (!newCategoryData.name.trim() || !viewingFramework) return;
        let updatedCategories;
        if (editingCategoryId) {
            updatedCategories = viewingFramework.categories.map(cat =>
                cat.id === editingCategoryId ? {
                    ...cat,
                    name: newCategoryData.name,
                    description: newCategoryData.description,
                    controls: newCategoryControls
                } : cat
            );
        } else {
            const newCategory = {
                id: Date.now().toString() + '-cat',
                name: newCategoryData.name,
                description: newCategoryData.description,
                controls: newCategoryControls
            };
            updatedCategories = [...(viewingFramework.categories || []), newCategory];
        }
        setViewingFramework({ ...viewingFramework, categories: updatedCategories });
        setFrameworks(prev => prev.map(fw =>
            fw.id === viewingFramework.id ? { ...fw, categories: updatedCategories } : fw
        ));
        cancelCategoryEdit();
    };

    const cancelCategoryEdit = () => {
        setIsAddingCategory(false);
        setEditingCategoryId(null);
        setNewCategoryData({ name: '', description: '' });
        setNewCategoryControls([{ ...defaultControl }]);
    };

    const handleDuplicateFramework = (fw, e) => {
        e.stopPropagation();
        const newFramework = {
            ...fw,
            id: Date.now().toString(),
            name: `${fw.name} (Copy)`,
            status: 'Draft',
            progress: 0,
            passingCount: 0,
            issuesCount: 0,
            tags: [...fw.tags.filter(t => t !== 'Active' && t !== 'Archived' && t !== 'Draft'), 'Draft'],
            categories: JSON.parse(JSON.stringify(fw.categories || []))
        };
        setFrameworks([...frameworks, newFramework]);
    };

    const handleArchiveFramework = (fw, e) => {
        e.stopPropagation();
        setFrameworks(prev => prev.map(f => f.id === fw.id ? {
            ...f, status: 'Archived', tags: [...f.tags.filter(t => t !== 'Active' && t !== 'Draft' && t !== 'Archived'), 'Archived']
        } : f));
    };

    const executeDeleteFramework = () => {
        if (!frameworkToDelete) return;
        setFrameworks(prev => prev.filter(fw => fw.id !== frameworkToDelete.id));
        setFrameworkToDelete(null);
    };

    const handleEditFramework = (fw, e) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditingId(fw.id);
        setFormData({
            name: fw.name,
            description: fw.description,
            type: fw.type,
            version: fw.version || '',
            owner: fw.owner || '',
            projects: fw.projects?.join(', ') || '',
            status: fw.status || 'Draft',
            category: ''
        });
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;

        const newTags = [formData.type === 'custom' ? 'Custom' : 'Standard', formData.status];

        if (isEditing) {
            setFrameworks(prev => prev.map(fw =>
                fw.id === editingId ? {
                    ...fw,
                    ...formData,
                    projects: formData.projects.split(',').map(p => p.trim()).filter(p => p),
                    tags: newTags
                } : fw
            ));
        } else {
            let generatedCategories = [];
            if (formData.type === 'standard') {
                const groups = controls.reduce((acc, ctrl) => {
                    const catName = ctrl.category?.trim() || 'Standard Domain';
                    if (!acc[catName]) acc[catName] = [];
                    const { category, ...cleanCtrl } = ctrl;
                    acc[catName].push(cleanCtrl);
                    return acc;
                }, {});

                generatedCategories = Object.entries(groups).map(([name, ctrls], idx) => ({
                    id: Date.now().toString() + `-cat-${idx}`,
                    name: name,
                    description: `Automatically grouped controls for ${name}`,
                    controls: ctrls
                }));
            }
            else {
                generatedCategories = [{
                    id: Date.now().toString() + '-cat',
                    name: formData.category || 'General Controls',
                    description: 'Initial mapped controls',
                    controls: controls
                }];
            }

            const newFramework = {
                id: Date.now().toString(),
                ...formData,
                projects: formData.projects.split(',').map(p => p.trim()).filter(p => p),
                tags: newTags,
                progress: 0,
                issuesCount: 0,
                passingCount: 0,
                categories: generatedCategories
            };
            setFrameworks([...frameworks, newFramework]);
        }
        closeModal();
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingId(null);
        setFormData({ name: '', description: '', type: 'custom', version: '', owner: '', projects: '', status: 'Draft', category: '' });
        setControls([{ ...defaultControl }]);
    };

    const closeViewingModal = () => {
        setViewingFramework(null);
        setShowControlsTable(false);
        cancelCategoryEdit();
        setSelectedCategory(null);
        setCategoryToDelete(null);
    };

    // --- Pagination States & Calculations ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // Calculate pagination values only if a category is selected
    const totalItems = selectedCategory?.controls?.length || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentControls = selectedCategory?.controls?.slice(indexOfFirstItem, indexOfLastItem) || [];

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const controlColumns = [
        {
            key: 'title',
            title: 'Control Title',
            headerClassName: 'w-[15%]',
            cellClassName: 'align-top',
            render: (ctrl) => (
                <span className="text-sm font-bold text-slate-900 line-clamp-2">{ctrl.title || '-'}</span>
            ),
        },
        {
            key: 'owner',
            title: 'Owner',
            headerClassName: 'w-[8%]',
            cellClassName: 'align-top',
            render: (ctrl) => (
                <span className="text-sm font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">{ctrl.owner || '-'}</span>
            ),
        },
        {
            key: 'status',
            title: 'Status',
            headerClassName: 'w-[8%]',
            cellClassName: 'align-top',
            render: (ctrl) => (
                <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg ring-1 shadow-sm shrink-0 whitespace-nowrap ${ctrl.status === 'Passing' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
                    ctrl.status === 'Failing' ? 'bg-rose-50 text-rose-700 ring-rose-200' :
                        ctrl.status === 'Needs Review' ? 'bg-amber-50 text-amber-700 ring-amber-200' :
                            'bg-slate-100 text-slate-600 ring-slate-200' // Not Started
                    }`}>
                    {ctrl.status || 'Not Started'}
                </span>
            ),
        },
        {
            key: 'scopeRef',
            title: 'Scope Ref',
            headerClassName: 'w-[8%]',
            cellClassName: 'align-top',
            render: (ctrl) => <span className="text-sm font-semibold text-slate-600">{ctrl.scopeRef || '-'}</span>
        },
        {
            key: 'complianceRef',
            title: 'Compliance Ref',
            headerClassName: 'w-[8%]',
            cellClassName: 'align-top',
            render: (ctrl) => (
                <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md ring-1 ring-indigo-200/50 whitespace-nowrap">{ctrl.complianceRef || '-'}</span>
            )
        },
        {
            key: 'globalStandard',
            title: 'Standard(s)',
            headerClassName: 'w-[10%]',
            cellClassName: 'align-top',
            render: (ctrl) => <span className="text-sm font-medium text-slate-700">{ctrl.globalStandard || '-'}</span>
        },
        {
            key: 'controlExpected',
            title: 'Control Expected',
            headerClassName: 'w-[23%]',
            cellClassName: 'align-top',
            render: (ctrl) => <span className="text-sm font-medium text-slate-800 leading-relaxed block min-w-[200px]" style={{ whiteSpace: "break-spaces" }}>{ctrl.controlExpected || '-'}</span>
        },
        {
            key: 'parameters',
            title: 'Parameters',
            headerClassName: 'w-[10%]',
            cellClassName: 'align-top',
            render: (ctrl) => <span className="text-sm font-medium text-slate-600">{ctrl.parameters || '-'}</span>
        },
        {
            key: 'evidence',
            title: 'Evidence',
            headerClassName: 'w-[10%]',
            cellClassName: 'align-top',
            render: (ctrl) => <span className="text-sm font-medium text-slate-600">{ctrl.evidence || '-'}</span>
        }
    ];

    return (
        <div className="">

            {/* FRAMEWORK DELETE CONFIRMATION MODAL */}
            {frameworkToDelete && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setFrameworkToDelete(null)}></div>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 sm:p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-rose-50/50">
                                <AlertTriangle size={32} strokeWidth={2} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Framework?</h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">
                                Are you sure you want to delete <span className="font-bold text-slate-800">"{frameworkToDelete.name}"</span>? This will permanently remove the framework and all its mapped controls.
                            </p>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                            <button onClick={() => setFrameworkToDelete(null)} className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
                                Cancel
                            </button>
                            <button onClick={executeDeleteFramework} className="flex-1 px-4 py-3 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all">
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CATEGORY DELETE CONFIRMATION MODAL */}
            {categoryToDelete && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={() => setCategoryToDelete(null)}></div>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 sm:p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto mb-5 ring-8 ring-rose-50/50">
                                <AlertTriangle size={32} strokeWidth={2} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Delete Category?</h3>
                            <p className="text-sm font-medium text-slate-500 leading-relaxed">
                                Are you sure you want to delete <span className="font-bold text-slate-800">"{categoryToDelete.name}"</span>? This will permanently remove this category and its <span className="font-bold">{categoryToDelete.controls?.length}</span> mapped controls.
                            </p>
                        </div>
                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                            <button onClick={() => setCategoryToDelete(null)} className="flex-1 px-4 py-3 text-sm font-bold text-slate-700 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-colors shadow-sm">
                                Cancel
                            </button>
                            <button onClick={executeDeleteCategory} className="flex-1 px-4 py-3 text-sm font-bold text-white bg-rose-600 rounded-xl hover:bg-rose-700 shadow-md shadow-rose-600/20 active:scale-95 transition-all">
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-[1400px] mx-auto animate-in fade-in zoom-in-[0.98] duration-400 ease-out">

                {/* CONDITIONAL RENDERING: PAGE VIEW OR GRID VIEW */}
                {viewingFramework ? (
                    /* --- VIEW CONTROLS PAGE --- */
                    <div className="">

                        {/* View Page Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => {
                                        if (isAddingCategory) {
                                            cancelCategoryEdit(); // Back to categories grid from add/edit form
                                        } else if (showControlsTable) {
                                            setShowControlsTable(false); // Back to categories grid from table view
                                        } else {
                                            closeViewingModal(); // Back to main framework dashboard
                                        }
                                    }}
                                    className="p-2.5 bg-white ring-1 ring-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 hover:ring-indigo-200 rounded-xl transition-all shadow-sm"
                                    title="Back"
                                >
                                    <ArrowLeft size={20} strokeWidth={2.5} />
                                </button>
                                <div>
                                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                                        <Layers className="text-indigo-600" size={24} />
                                        {isAddingCategory
                                            ? (editingCategoryId ? 'Edit Category' : 'Add New Category')
                                            : showControlsTable ? `${selectedCategory?.name} Controls` : `${viewingFramework.name} Categories`}
                                    </h2>
                                    <p className="text-sm font-semibold text-slate-500 mt-1">
                                        {isAddingCategory
                                            ? "Define category details and build its list of controls."
                                            : showControlsTable ? "Viewing all controls mapped to this category." : "Select a category to view its associated controls."}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 ml-14 md:ml-0">
                                {/* Only show Add Category if not inside a table, not already adding, and if Custom framework */}
                                {!showControlsTable && !isAddingCategory && viewingFramework.type !== 'standard' && (
                                    <button
                                        onClick={() => {
                                            setIsAddingCategory(true);
                                            setEditingCategoryId(null);
                                            setNewCategoryData({ name: '', description: '' });
                                            setNewCategoryControls([{ ...defaultControl }]);
                                        }}
                                        className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                                    >
                                        <Plus size={16} strokeWidth={3} /> Add Category
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* View Page Body */}
                        <div className="flex-1 bg-slate-50/50">

                            {/* STEP 3: ADD / EDIT CATEGORY & CONTROLS VIEW */}
                            {isAddingCategory ? (
                                <div className="flex flex-col h-full animate-in fade-in duration-300 max-w-6xl mx-auto">


                                    {/* Category General Info */}
                                    <div className="bg-white p-6 rounded-3xl ring-1 ring-slate-200/60 shadow-sm mb-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Category Name <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="text" value={newCategoryData.name} onChange={(e) => setNewCategoryData({ ...newCategoryData, name: e.target.value })}
                                                    className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="e.g. Privacy Controls" autoFocus
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Description</label>
                                                <input
                                                    type="text" value={newCategoryData.description} onChange={(e) => setNewCategoryData({ ...newCategoryData, description: e.target.value })}
                                                    className="w-full bg-slate-50 ring-1 ring-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                    placeholder="Brief description of these controls..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dynamic Controls List for Category */}
                                    <div className="space-y-6">
                                        {newCategoryControls.map((ctrl, index) => (
                                            <div key={ctrl.id} className="p-6 rounded-2xl ring-1 relative group transition-all bg-white ring-slate-200/80 hover:shadow-md hover:ring-indigo-200">

                                                {newCategoryControls.length > 1 && (
                                                    <button
                                                        type="button" onClick={() => removeNewCategoryControl(ctrl.id)}
                                                        className="absolute -top-3 -right-3 bg-white ring-1 ring-slate-200 text-rose-500 p-2 rounded-full shadow-md hover:bg-rose-50 hover:ring-rose-200 transition-all opacity-0 group-hover:opacity-100 z-10"
                                                    >
                                                        <Trash2 size={14} strokeWidth={2.5} />
                                                    </button>
                                                )}

                                                <div className="flex items-center gap-3 mb-5">
                                                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-black ring-1 ring-indigo-200/50 shadow-sm">
                                                        {index + 1}
                                                    </span>
                                                    <h4 className="text-sm font-bold text-slate-800">Control Definition</h4>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                                    {/* NEW FIELDS */}
                                                    <div className="md:col-span-2 lg:col-span-3 xl:col-span-2">
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Control Title</label>
                                                        <input type="text" value={ctrl.title} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'title', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. Password Policy" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Owner</label>
                                                        <input type="text" value={ctrl.owner} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'owner', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. IT Dept" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Status</label>
                                                        <div className="relative">
                                                            <select value={ctrl.status} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'status', e.target.value)} className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer">
                                                                <option value="Not Started">Not Started</option>
                                                                <option value="Passing">Passing</option>
                                                                <option value="Failing">Failing</option>
                                                                <option value="Needs Review">Needs Review</option>
                                                            </select>
                                                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                                        </div>
                                                    </div>
                                                    {/* ORIGINAL FIELDS */}
                                                    <div>
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Scope Reference</label>
                                                        <input type="text" value={ctrl.scopeRef} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'scopeRef', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. All Systems" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Compliance (Ref)</label>
                                                        <input type="text" value={ctrl.complianceRef} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'complianceRef', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. CC6.1" />
                                                    </div>
                                                    <div className="md:col-span-2 lg:col-span-1 xl:col-span-2">
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Global Standard(s)</label>
                                                        <input type="text" value={ctrl.globalStandard} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'globalStandard', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. ISO 27001" />
                                                    </div>
                                                    <div className="md:col-span-2 lg:col-span-3 xl:col-span-4">
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Control Expected</label>
                                                        <textarea value={ctrl.controlExpected} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'controlExpected', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-medium h-20 focus:ring-2 focus:ring-indigo-500 outline-none resize-none transition-all" placeholder="Describe the expected control behavior..."></textarea>
                                                    </div>
                                                    <div className="md:col-span-1 lg:col-span-1.5 xl:col-span-2">
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Parameters to Check</label>
                                                        <input type="text" value={ctrl.parameters} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'parameters', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. In-Scope Only" />
                                                    </div>
                                                    <div className="md:col-span-1 lg:col-span-1.5 xl:col-span-2">
                                                        <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Evidence Inputs</label>
                                                        <input type="text" value={ctrl.evidence} onChange={(e) => handleNewCategoryControlChange(ctrl.id, 'evidence', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. Policies, Logs" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        type="button" onClick={addNewCategoryControl}
                                        className="w-full py-4 border-2 border-dashed border-slate-300 text-slate-600 font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-50 hover:border-indigo-300 hover:text-indigo-600 transition-all mt-6"
                                    >
                                        <Plus size={18} strokeWidth={2.5} /> Add Another Control
                                    </button>

                                    <div className="mt-6 flex items-center justify-between">
                                        <button
                                            onClick={cancelCategoryEdit}
                                            className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-rose-600 transition-colors px-4 py-2 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-rose-50 hover:ring-rose-200 shadow-sm"
                                        >
                                            <ArrowLeft size={16} strokeWidth={2.5} /> {editingCategoryId ? 'Cancel Editing' : 'Cancel Addition'}
                                        </button>
                                        <button
                                            onClick={handleSaveNewCategory}
                                            disabled={!newCategoryData.name.trim()}
                                            className="flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 px-6 py-2.5 rounded-lg hover:bg-indigo-700 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Save size={16} strokeWidth={2.5} /> {editingCategoryId ? 'Update Category' : 'Save Category'}
                                        </button>
                                    </div>
                                </div>

                                // STEP 1: CATEGORY CARDS VIEW
                            ) : !showControlsTable ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-300">
                                    {(!viewingFramework.categories || viewingFramework.categories.length === 0) ? (
                                        <div className="col-span-full text-center py-20 bg-white ring-1 ring-slate-200 rounded-3xl border-dashed">
                                            <Shield className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                            <h3 className="text-lg font-bold text-slate-700 mb-1">No Categories Found</h3>
                                            <p className="text-slate-500 text-sm">Create a category to start organizing controls.</p>
                                        </div>
                                    ) : (
                                        viewingFramework.categories.map((cat) => (
                                            <div
                                                key={cat.id}
                                                onClick={() => { setSelectedCategory(cat); setShowControlsTable(true); }}
                                                className="bg-white rounded-3xl p-6 ring-1 ring-slate-200/60 shadow-sm cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:ring-indigo-300 transition-all group flex flex-col relative overflow-hidden"
                                            >
                                                <div className="flex justify-between items-start mb-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl group-hover:scale-110 transition-transform">
                                                            <Layers size={24} strokeWidth={2.5} />
                                                        </div>
                                                        <span className="bg-slate-100 text-slate-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg">
                                                            Category
                                                        </span>
                                                    </div>

                                                    {/* Action Buttons (Edit / Delete) - Only show if not standard framework */}
                                                    {viewingFramework.type !== 'standard' && (
                                                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity absolute top-5 right-5 bg-white/90 backdrop-blur shadow-sm rounded-lg ring-1 ring-slate-200/60 p-0.5 z-10">
                                                            <button
                                                                onClick={(e) => handleEditCategoryStart(cat, e)}
                                                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-all"
                                                                title="Edit Category"
                                                            >
                                                                <Edit3 size={14} strokeWidth={2.5} />
                                                            </button>
                                                            <div className="w-px h-4 bg-slate-200"></div>
                                                            <button
                                                                onClick={(e) => confirmDeleteCategory(cat, e)}
                                                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-all"
                                                                title="Delete Category"
                                                            >
                                                                <Trash2 size={14} strokeWidth={2.5} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <h4 className="text-xl font-black text-slate-900 mb-2 pr-12">{cat.name}</h4>
                                                <p className="text-sm font-medium text-slate-500 mb-6 flex-1 line-clamp-2">
                                                    {cat.description || 'Access and manage all compliance controls associated with this category.'}
                                                </p>

                                                <div className="pt-5 border-t border-slate-100 flex items-center justify-between mt-auto">
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Controls</span>
                                                        <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                                                            <Shield size={14} className="text-indigo-500" /> {cat.controls.length} Mapped
                                                        </span>
                                                    </div>
                                                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-indigo-600 group-hover:text-white transition-colors shadow-sm">
                                                        <ArrowRight size={16} strokeWidth={2.5} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                // STEP 2: CONTROLS TABLE VIEW
                            ) : (
                                <div className="flex flex-col h-full animate-in slide-in-from-right-4 fade-in duration-300">
                                    {(!selectedCategory.controls || selectedCategory.controls.length === 0) ? (
                                        <div className="text-center py-20 bg-white ring-1 ring-slate-200 rounded-3xl border-dashed">
                                            <Shield className="mx-auto h-12 w-12 text-slate-300 mb-4" />
                                            <h3 className="text-lg font-bold text-slate-700 mb-1">No controls configured</h3>
                                        </div>
                                    ) : (
                                        <ReusableTable
                                            columns={controlColumns}
                                            data={selectedCategory.controls}
                                            pagination={{
                                                currentPage,
                                                totalPages,
                                                onPageChange: paginate,
                                                indexOfFirstItem,
                                                indexOfLastItem,
                                                totalItems: selectedCategory.controls.length
                                            }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                ) : (

                    /* --- DASHBOARD GRID VIEW --- */
                    <>
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white hidden sm:block">
                                    <Layers size={28} strokeWidth={2} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Frameworks & Controls</h3>
                                    <p className="text-sm font-semibold text-slate-500 mt-0.5">Manage and configure your security and compliance standards.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
                            >
                                <PlusCircle size={18} /> Create Framework
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {frameworks.length === 0 ? (
                                <div className="col-span-full text-center py-20 bg-white border-2 border-slate-200 rounded-[2rem] border-dashed">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-slate-200 shadow-sm">
                                        <Layers className="h-10 w-10 text-slate-400" />
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 mb-2">No frameworks yet</h3>
                                    <p className="text-sm font-medium text-slate-500 max-w-sm mx-auto">Get started by creating your first compliance framework to begin tracking controls.</p>
                                </div>
                            ) : (
                                frameworks.map((fw) => (
                                    <div key={fw.id} className="bg-white ring-1 ring-slate-200/60 rounded-3xl shadow-sm overflow-hidden transition-all hover:shadow-xl hover:shadow-indigo-500/5 hover:-translate-y-1 hover:ring-indigo-200 flex flex-col">

                                        {/* Card Header & Content */}
                                        <div className="p-6 flex-1 flex flex-col">
                                            <div className="flex justify-between items-start mb-4">
                                                <h3 className="font-extrabold text-slate-900 text-xl tracking-tight">{fw.name}</h3>
                                                <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-md ring-1 shadow-sm shrink-0 ${fw.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
                                                    fw.status === 'Archived' ? 'bg-slate-100 text-slate-600 ring-slate-200' :
                                                        'bg-amber-50 text-amber-700 ring-amber-200'
                                                    }`}>
                                                    {fw.status}
                                                </span>
                                            </div>

                                            <p className="text-sm text-slate-500 font-medium leading-relaxed line-clamp-2 mb-6">
                                                {fw.description}
                                            </p>

                                            {/* UPDATED: Metrics Section (Controls Summary) */}
                                            <div className="bg-slate-50 rounded-2xl p-4 ring-1 ring-slate-200/60 mb-6 flex items-center justify-between group-hover:bg-indigo-50/30 transition-colors">
                                                <div className="flex items-center gap-3.5">
                                                    <div className="p-2.5 bg-white text-indigo-600 rounded-xl ring-1 ring-slate-200 shadow-sm">
                                                        <CheckCircle2 size={18} strokeWidth={2.5} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Controls Mapped</span>
                                                        <div className="flex items-baseline gap-1">
                                                            <span className="text-lg font-black text-slate-900 leading-none">{fw.passingCount}</span>
                                                            <span className="text-sm font-bold text-slate-400">
                                                                / {fw.categories?.reduce((acc, cat) => acc + cat.controls.length, 0) || 0}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Dynamic Percentage Badge */}
                                                {(() => {
                                                    const total = fw.categories?.reduce((acc, cat) => acc + cat.controls.length, 0) || 0;
                                                    const percent = total > 0 ? Math.round((fw.passingCount / total) * 100) : 0;
                                                    return (
                                                        <div className="px-2.5 py-1 bg-white ring-1 ring-slate-200 rounded-lg shadow-sm flex items-center gap-1.5">
                                                            <span className={`w-1.5 h-1.5 rounded-full ${percent === 100 ? 'bg-emerald-500' : percent > 0 ? 'bg-amber-400' : 'bg-slate-300'}`}></span>
                                                            <span className="text-xs font-bold text-slate-700">{percent}%</span>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-2 mt-auto">
                                                {fw.tags?.map((tag, idx) => (
                                                    <span key={idx} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-widest rounded-md ring-1 ring-slate-200/60">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Card Footer Actions */}
                                        <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 mt-auto">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setViewingFramework(fw);
                                                    setShowControlsTable(false);
                                                    cancelCategoryEdit();
                                                }}
                                                className="text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-1"
                                            >
                                                View Controls
                                            </button>

                                            <div className="flex items-center gap-1">
                                                <button onClick={(e) => handleEditFramework(fw, e)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit">
                                                    <Edit3 size={16} />
                                                </button>
                                                <button onClick={(e) => handleDuplicateFramework(fw, e)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="Duplicate">
                                                    <Copy size={16} />
                                                </button>
                                                <button onClick={(e) => handleArchiveFramework(fw, e)} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all" title="Archive">
                                                    <Archive size={16} />
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); setFrameworkToDelete(fw); }} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all" title="Delete">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}

                {/* MAIN CREATE/EDIT MODAL OVERLAY */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4 md:p-6">
                        <div className="absolute inset-0 transition-opacity" onClick={closeModal}></div>
                        <div className="bg-white rounded-[2rem] w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl relative z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                            {/* Modal Header */}
                            <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-white z-20">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                                        {isEditing ? <Edit3 size={20} strokeWidth={2.5} /> : <Plus size={20} strokeWidth={2.5} />}
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                                            {isEditing ? 'Edit Framework' : 'Create Framework'}
                                        </h2>
                                    </div>
                                </div>
                                <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                                    <X size={20} strokeWidth={2.5} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="overflow-y-auto flex-1 p-8 space-y-8 bg-slate-50/30 custom-scrollbar">

                                {/* Section 1: General Info */}
                                <section className="bg-white p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm space-y-6">
                                    <h3 className="font-black text-slate-800 flex items-center gap-2 mb-2 uppercase tracking-wider text-sm">
                                        <FileText size={18} className="text-indigo-500" /> General Information
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-sm font-black text-slate-700 mb-3">Framework Type</label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div
                                                    onClick={() => setFormData(prev => ({ ...prev, type: 'standard' }))}
                                                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-3 ${formData.type === 'standard'
                                                        ? 'border-slate-300 bg-white shadow-sm'
                                                        : 'border-slate-100 hover:border-slate-200 bg-slate-50/50'
                                                        }`}
                                                >
                                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${formData.type === 'standard' ? 'text-slate-500' : 'text-slate-300'}`}>
                                                        <CheckCircle2 size={24} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-base font-bold text-slate-800 mb-1">Standard Framework</h4>
                                                        <p className="text-sm font-medium text-slate-500">Standard controls / No changes can be done.</p>
                                                    </div>
                                                </div>

                                                <div
                                                    onClick={() => setFormData(prev => ({ ...prev, type: 'custom' }))}
                                                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all flex flex-col gap-3 ${formData.type === 'custom'
                                                        ? 'border-amber-400 bg-amber-50/30 shadow-sm'
                                                        : 'border-slate-100 hover:border-amber-200 bg-slate-50/50'
                                                        }`}
                                                >
                                                    <div className={`w-7 h-7 flex items-center justify-center ${formData.type === 'custom' ? 'text-amber-500' : 'text-slate-300'}`}>
                                                        <Edit3 size={24} />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-base font-bold text-slate-800 mb-1">Custom Framework</h4>
                                                        <p className="text-sm font-medium text-slate-500">Add / Modify / Delete Controls.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* CONDITIONAL: First Category Input (Only when Creating a Custom Framework) */}
                                        {!isEditing && formData.type === 'custom' && (
                                            <div className="col-span-1 md:col-span-2 animate-in fade-in slide-in-from-top-2 duration-200">
                                                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Category Name <span className="text-rose-500">*</span></label>
                                                <input
                                                    type="text" name="category" value={formData.category || ''} onChange={handleInputChange}
                                                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-400 shadow-sm transition-all placeholder-slate-400"
                                                    placeholder="e.g. Privacy, Security, Operations..." required={formData.type === 'custom'}
                                                />
                                            </div>
                                        )}

                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Framework Name <span className="text-rose-500">*</span></label>
                                            <input
                                                type="text" name="name" value={formData.name} onChange={handleInputChange}
                                                className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                                placeholder="e.g. SOC 2 Type II" autoFocus required
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Description</label>
                                            <textarea
                                                name="description" value={formData.description} onChange={handleInputChange}
                                                className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all resize-none placeholder-slate-400 h-24"
                                                placeholder="Briefly describe the objective of this framework..."
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Version</label>
                                            <input
                                                type="text" name="version" value={formData.version} onChange={handleInputChange}
                                                className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                                placeholder="e.g. SOC2 2017"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Framework Owner</label>
                                            <input
                                                type="text" name="owner" value={formData.owner} onChange={handleInputChange}
                                                className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                                placeholder="e.g. Compliance Team"
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Applicable Projects</label>
                                            <input
                                                type="text" name="projects" value={formData.projects} onChange={handleInputChange}
                                                className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                                                placeholder="Comma separated project names..."
                                            />
                                        </div>
                                        <div className="col-span-1 md:col-span-2">
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Status</label>
                                            <div className="relative">
                                                <select
                                                    name="status" value={formData.status} onChange={handleInputChange}
                                                    className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all cursor-pointer"
                                                >
                                                    <option value="Draft">Draft</option>
                                                    <option value="Active">Active</option>
                                                    <option value="Archived">Archived</option>
                                                </select>
                                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Section 2: Controls Management (Only displayed when Creating) */}
                                {!isEditing ? (
                                    <section className="bg-white p-8 rounded-3xl ring-1 ring-slate-200/60 shadow-sm space-y-6">
                                        <div className="flex justify-between items-end mb-4 border-b border-slate-100 pb-3">
                                            <h3 className="font-black text-slate-800 flex items-center gap-2 uppercase tracking-wider text-sm">
                                                <Layers size={18} className="text-indigo-500" /> Initial Controls List
                                            </h3>
                                            <span className="text-xs font-black text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-lg ring-1 ring-indigo-200/50">
                                                {controls.length} Defined
                                            </span>
                                        </div>

                                        {formData.type === 'standard' && (
                                            <div className="bg-indigo-50 text-indigo-800 p-4 rounded-xl text-sm mb-6 ring-1 ring-indigo-200/50 flex items-start gap-3">
                                                <Layers size={18} className="mt-0.5 shrink-0 text-indigo-600" />
                                                <p className="font-medium">You are creating a <strong>Standard Framework</strong>. Categories will be automatically generated based on the <strong>Control Category</strong> you assign to each control below.</p>
                                            </div>
                                        )}

                                        <div className="space-y-6">
                                            {controls.map((ctrl, index) => (
                                                <div key={ctrl.id} className="p-6 rounded-2xl ring-1 relative group transition-all bg-white ring-slate-200/80 hover:shadow-md hover:ring-indigo-200">

                                                    {controls.length > 1 && (
                                                        <button type="button" onClick={() => removeControl(ctrl.id)} className="absolute -top-3 -right-3 bg-white ring-1 ring-slate-200 text-rose-500 p-2 rounded-full shadow-md hover:bg-rose-50 hover:ring-rose-200 transition-all opacity-0 group-hover:opacity-100 z-10">
                                                            <Trash2 size={14} strokeWidth={2.5} />
                                                        </button>
                                                    )}

                                                    <div className="flex items-center gap-3 mb-5">
                                                        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-black ring-1 ring-indigo-200/50 shadow-sm">
                                                            {index + 1}
                                                        </span>
                                                        <h4 className="text-sm font-bold text-slate-800">Control Definition</h4>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">

                                                        {/* DYNAMIC CATEGORY INPUT FOR STANDARD FRAMEWORKS ONLY */}
                                                        {formData.type === 'standard' && (
                                                            <div className="md:col-span-2 lg:col-span-3 xl:col-span-4">
                                                                <label className="block text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1.5">Control Category <span className="text-rose-500">*</span></label>
                                                                <input type="text" value={ctrl.category || ''} onChange={(e) => handleControlChange(ctrl.id, 'category', e.target.value)} className="w-full ring-2 ring-indigo-100 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. Security, Privacy, Access Control..." required />
                                                            </div>
                                                        )}

                                                        {/* NEW FIELDS */}
                                                        <div className="md:col-span-2 lg:col-span-3 xl:col-span-2">
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Control Title</label>
                                                            <input type="text" value={ctrl.title} onChange={(e) => handleControlChange(ctrl.id, 'title', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. Password Policy" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Owner</label>
                                                            <input type="text" value={ctrl.owner} onChange={(e) => handleControlChange(ctrl.id, 'owner', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. IT Dept" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Status</label>
                                                            <div className="relative">
                                                                <select value={ctrl.status} onChange={(e) => handleControlChange(ctrl.id, 'status', e.target.value)} className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all cursor-pointer">
                                                                    <option value="Not Started">Not Started</option>
                                                                    <option value="Passing">Passing</option>
                                                                    <option value="Failing">Failing</option>
                                                                    <option value="Needs Review">Needs Review</option>
                                                                </select>
                                                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                                                            </div>
                                                        </div>

                                                        {/* ORIGINAL FIELDS */}
                                                        <div>
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Scope Reference</label>
                                                            <input type="text" value={ctrl.scopeRef} onChange={(e) => handleControlChange(ctrl.id, 'scopeRef', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. All Systems" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Compliance (Ref)</label>
                                                            <input type="text" value={ctrl.complianceRef} onChange={(e) => handleControlChange(ctrl.id, 'complianceRef', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. CC6.1" />
                                                        </div>
                                                        <div className="md:col-span-2 lg:col-span-1 xl:col-span-2">
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Global Standard(s)</label>
                                                            <input type="text" value={ctrl.globalStandard} onChange={(e) => handleControlChange(ctrl.id, 'globalStandard', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. ISO 27001" />
                                                        </div>
                                                        <div className="md:col-span-2 lg:col-span-3 xl:col-span-4">
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Control Expected</label>
                                                            <textarea value={ctrl.controlExpected} onChange={(e) => handleControlChange(ctrl.id, 'controlExpected', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-medium h-20 focus:ring-2 focus:ring-indigo-500 outline-none resize-none transition-all" placeholder="Describe the expected control behavior..."></textarea>
                                                        </div>
                                                        <div className="md:col-span-1 lg:col-span-1.5 xl:col-span-2">
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Parameters to Check</label>
                                                            <input type="text" value={ctrl.parameters} onChange={(e) => handleControlChange(ctrl.id, 'parameters', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. In-Scope Only" />
                                                        </div>
                                                        <div className="md:col-span-1 lg:col-span-1.5 xl:col-span-2">
                                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Evidence Inputs</label>
                                                            <input type="text" value={ctrl.evidence} onChange={(e) => handleControlChange(ctrl.id, 'evidence', e.target.value)} className="w-full ring-1 ring-slate-200 rounded-xl p-3 text-sm font-semibold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" placeholder="e.g. Policies, Logs" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <button type="button" onClick={addControl} className="w-full py-4 border-2 border-dashed border-slate-300 text-slate-600 font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-50 hover:border-indigo-300 hover:text-indigo-600 transition-all mt-4">
                                            <Plus size={18} strokeWidth={2.5} /> Add Another Control
                                        </button>
                                    </section>
                                ) : (
                                    <div className="bg-indigo-50 p-6 rounded-3xl ring-1 ring-indigo-100 flex items-start gap-4">
                                        <div className="bg-indigo-100 p-3 rounded-2xl text-indigo-600">
                                            <Layers size={24} strokeWidth={2.5} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-indigo-900 mb-1 text-lg tracking-tight">Manage Categories & Controls</h4>
                                            <p className="text-sm font-medium text-indigo-700 leading-relaxed">To add or modify categories and their specific controls, please save your framework changes here first. Then, use the <span className="font-bold">"View Controls"</span> button on the framework dashboard.</p>
                                        </div>
                                    </div>
                                )}

                            </div>

                            {/* Modal Footer */}
                            <div className="px-8 py-6 border-t border-slate-100 bg-white flex justify-end gap-3 z-20">
                                <button
                                    type="button" onClick={closeModal}
                                    className="px-6 py-3 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-all shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button" onClick={handleSubmit}
                                    className="px-8 py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl shadow-md hover:bg-indigo-700 hover:shadow-lg active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!formData.name.trim() || (!isEditing && formData.type === 'custom' && !formData.category?.trim())}
                                >
                                    {isEditing ? 'Save Changes' : 'Save Framework'}
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default Framework;